<template>
	<div 
		class="selector-card"
		:class="{ 
			'selector-card--selected': modelValue,
			'selector-card--disabled': disabled
		}"
		role="button"
		:tabindex="disabled ? -1 : 0"
		:aria-pressed="modelValue"
		:aria-disabled="disabled"
		:aria-describedby="description ? `${sanitizedId}-desc` : undefined"
		@click="handleClick"
		@keydown.enter="handleClick"
		@keydown.space.prevent="handleClick"
	>
		<div class="selector-card__content">
			<div class="selector-card__icon">
				<slot v-if="$slots.icon" name="icon">
				</slot>
			</div>
			<div class="selector-card__text">
				<h3 class="selector-card__title">
					<slot name="title">{{ title }}</slot>
				</h3>
				<p 
					v-if="description" 
					class="selector-card__description"
					:id="`${sanitizedId}-desc`"
				>
					{{ description }}
				</p>
			</div>
		</div>
		<div class="selector-card__check" role="img" :aria-label="modelValue ? 'Selected' : 'Not selected'">
			<v-icon 
				v-if="modelValue" 
				color="primary" 
				size="28"
				aria-hidden="true"
			>
				mdi-check
			</v-icon>
			<!-- Screen reader text for state -->
			<span class="sr-only">{{ modelValue ? 'Selected' : 'Not selected' }}</span>
		</div>
	</div>
</template>

<script lang="ts">
import Vue from 'vue'

export interface SelectorCardProps {
	/** Whether the card is selected */
	modelValue?: boolean
	/** The main title text */
	title: string
	/** Optional description text */
	description?: string
	/** Whether the card is disabled */
	disabled?: boolean
}

export default Vue.extend({
	name: 'SelectorCard',
	props: {
		modelValue: {
			type: Boolean,
			default: false
		},
		title: {
			type: String,
			required: true
		},
		description: {
			type: String,
			default: ''
		},
		disabled: {
			type: Boolean,
			default: false
		}
	},
	computed: {
		/**
		 * Sanitized ID safe for use in DOM attributes
		 * Removes potentially harmful characters and ensures safe ID generation
		 */
		sanitizedId(): string {
			// Remove all characters that aren't alphanumeric, spaces, hyphens, or underscores
			// Then replace spaces with hyphens and convert to lowercase
			return this.title
				.replace(/[^a-zA-Z0-9\s\-_]/g, '')
				.replace(/\s+/g, '-')
				.toLowerCase()
				.replace(/^-+|-+$/g, '') // Remove leading/trailing hyphens
				|| 'selector-card' // Fallback if title becomes empty after sanitization
		}
	},
	methods: {
		handleClick() {
			if (!this.disabled) {
				this.$emit('input', !this.modelValue)
				this.$emit('update:modelValue', !this.modelValue)
			}
		}
	}
})
</script>

<style lang="scss" scoped>
.selector-card {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 16px 20px;
	border: 2px solid var(--global-border-color, #e0e0e0);
	border-radius: var(--radius);
	background: var(--global-card-background, #ffffff);
	cursor: pointer;
	transition: all 0.2s ease-in-out;
	min-height: 72px;

	&:hover:not(.selector-card--selected):not(.selector-card--disabled) {
		border-color: var(--global-border-color-hover, #c5c5c5);
		box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
	}

	&:focus {
		outline: 2px solid var(--global-primary, #406A94);
		outline-offset: 2px;
	}

	&:focus:not(:focus-visible) {
		outline: none;
	}

	&:focus-visible {
		outline: 2px solid var(--global-primary, #406A94);
		outline-offset: 2px;
	}

	&--selected {
		border-color: var(--global-primary, #406A94);
		background: var(--selector-card-selected-bg, rgba(67, 106, 148, 0.05));
	}

	&--disabled {
		cursor: not-allowed;
		opacity: 0.6;

		&:hover {
			border-color: var(--global-border-color, #e0e0e0);
			box-shadow: none;
		}
	}
}

.selector-card__content {
	display: flex;
	align-items: center;
	gap: 12px;
	flex: 1;
}

.selector-card__icon {
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
}

.selector-card__text {
	flex: 1;
}

.selector-card__title {
	margin: 0;
	font-size: 16px;
	font-weight: 600;
	color: var(--global-header-text-color, #212121);
	line-height: 1.3;
}

.selector-card__description {
	margin: 4px 0 0 0;
	font-size: 14px;
	color: var(--global-muted-foreground, #757575);
	line-height: 1.4;
}

.selector-card__check {
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	margin-left: 12px;
	width: 24px;
	height: 24px;
}

/* Screen reader only content */
.sr-only {
	position: absolute;
	width: 1px;
	height: 1px;
	padding: 0;
	margin: -1px;
	overflow: hidden;
	clip: rect(0, 0, 0, 0);
	white-space: nowrap;
	border: 0;
}
</style>

<!-- Unscoped styles for dark mode overrides -->
<style lang="scss">
.dark .selector-card,
.theme--dark .selector-card {
	background: var(--global-card-background, #1c1c1e);
	border-color: var(--global-border-color, #2e2e32);

	&:hover:not(.selector-card--selected):not(.selector-card--disabled) {
		border-color: var(--global-border-color-hover, #3e3e42);
		box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
	}

	&.selector-card--selected {
		border-color: var(--global-primary, #ed303e);
		background: var(--selector-card-selected-bg, rgba(237, 48, 62, 0.1));
	}

	&.selector-card--disabled:hover {
		border-color: var(--global-border-color, #2e2e32);
	}
}

.dark .selector-card__title,
.theme--dark .selector-card__title {
	color: var(--global-foreground, #f5f5f5);
}

.dark .selector-card__description,
.theme--dark .selector-card__description {
	color: var(--global-muted-foreground, #9ca3af);
}
</style>